// ---------------------------------------------------------------------------
// Test Mode — lead meeting notes, actions & interaction timeline (CLE-261).
//
// Every simulated lead (ld1..ld15) gets:
//   - >= 2 meeting notes (MeetingNote scoped by leadId) with realistic Romanian
//     content (proiect, buget, timeline, cerinte tehnice), decizii & follow-ups
//   - >= 5 interactions: a mix of emails, calls, meetings (linked to a note),
//     WhatsApp/chat messages and status changes, rendered as the lead activity
//     timeline (LeadTimelineItem = activity | comment) plus LeadAction entries
//   - chronologically coherent dates anchored to each lead's age
//   - real Clean Code Culture SRL team members as authors/attendees
//
// These drive the lead detail page (CLE-192/205):
//   GET /api/meeting-notes?leadId=<id>   -> meetingNotesForLead
//   GET /api/leads/:id/timeline          -> leadTimeline
//   GET /api/leads/:id/actions           -> leadActions
// ---------------------------------------------------------------------------

import type { MeetingNote, MeetingType } from '../lib/meetingNotesApi';
import type {
  LeadAction,
  LeadActionType,
  LeadActivity,
  LeadComment,
  LeadTimelineItem,
} from '../lib/leadsApi';
import { isoDate, isoDateTime } from './company';
import { PEOPLE_REFS } from './people';

function personName(id: string): string {
  return PEOPLE_REFS.find((p) => p.id === id)?.name ?? 'Necunoscut';
}

function ref(id: string): { id: string; name: string } {
  return { id, name: personName(id) };
}

// --- Authoring shapes ------------------------------------------------------

interface NoteSeed {
  key: string;
  title: string;
  dayOffset: number; // days from "today" (negative = past)
  attendees: string;
  meetingType: MeetingType;
  content: string;
  summary: string;
  decisions: string;
  actionItems: string;
  keyFacts: string;
  openQuestions: string;
}

type InteractionKind = 'email' | 'call' | 'meeting' | 'message' | 'status' | 'comment';

interface InteractionSeed {
  kind: InteractionKind;
  dayOffset: number;
  hour: number;
  who: string; // person id
  // For status changes:
  field?: string;
  oldValue?: string | null;
  newValue?: string | null;
  // For email/call/message/comment text:
  text?: string;
}

interface ActionSeed {
  type: LeadActionType;
  title: string;
  dayOffset: number; // due date offset
  who: string;
  completed: boolean;
  completedOffset?: number;
}

// --- Builders --------------------------------------------------------------

function toNote(leadId: string, seed: NoteSeed): MeetingNote {
  return {
    id: `mn-${leadId}-${seed.key}`,
    clientId: null,
    projectId: null,
    leadId,
    personId: null,
    meetingId: null,
    title: seed.title,
    date: isoDate(seed.dayOffset),
    attendees: seed.attendees,
    meetingType: seed.meetingType,
    content: seed.content,
    summary: seed.summary,
    decisions: seed.decisions,
    actionItems: seed.actionItems,
    keyFacts: seed.keyFacts,
    openQuestions: seed.openQuestions,
    source: 'transcribed',
    s3Key: null,
    s3Url: null,
    audioKey: null,
    ingestStatus: 'COMPLETED',
    ingestLog: null,
    lastIngestedAt: isoDateTime(seed.dayOffset, 16),
    isIndexed: true,
    createdById: null,
    createdAt: isoDateTime(seed.dayOffset, 15),
    updatedAt: isoDateTime(seed.dayOffset, 16),
  };
}

// An interaction renders as either an activity event (email/call/meeting/
// message/status) or a comment (the rep's written follow-up note).
function toTimelineItem(leadId: string, idx: number, s: InteractionSeed): LeadTimelineItem {
  const at = isoDateTime(s.dayOffset, s.hour);
  if (s.kind === 'comment') {
    const comment: LeadComment = {
      id: `lc-${leadId}-${idx}`,
      leadId,
      authorId: s.who,
      author: ref(s.who),
      content: s.text ?? '',
      attachments: [],
      createdAt: at,
      updatedAt: at,
    };
    return { kind: 'comment', at, data: comment };
  }

  // Map the interaction kind to an activity `field` the timeline understands.
  // email/call/message/meeting use a synthetic "interaction_<kind>" field so the
  // default activityText branch renders a readable Romanian phrase; status
  // changes use the real lead field names so they read naturally.
  let field: string;
  let oldValue: string | null = null;
  let newValue: string | null = null;
  if (s.kind === 'status') {
    field = s.field ?? 'stageId';
    oldValue = s.oldValue ?? null;
    newValue = s.newValue ?? null;
  } else {
    field = `interaction_${s.kind}`;
    newValue = s.text ?? null;
  }
  const activity: LeadActivity = {
    id: `la-${leadId}-${idx}`,
    leadId,
    userId: s.who,
    user: ref(s.who),
    field,
    oldValue,
    newValue,
    createdAt: at,
  };
  return { kind: 'activity', at, data: activity };
}

function toAction(leadId: string, idx: number, s: ActionSeed): LeadAction {
  const created = isoDateTime(s.dayOffset - 5, 9);
  return {
    id: `lact-${leadId}-${idx}`,
    leadId,
    type: s.type,
    title: s.title,
    description: null,
    assigneeId: s.who,
    assignee: ref(s.who),
    dueDate: isoDateTime(s.dayOffset, 10),
    completedAt: s.completed ? isoDateTime(s.completedOffset ?? s.dayOffset, 11) : null,
    isCompleted: s.completed,
    createdAt: created,
    updatedAt: isoDateTime(s.dayOffset, 11),
  };
}

// --- Per-lead context (mirrors testData/leads.ts seeds) --------------------

interface LeadCtx {
  title: string;
  company: string;
  contact: string;
  valueEur: number;
  assignee: string; // person id (sales owner)
  techLead: string; // person id (dev/PM joining calls)
  stage: 'new' | 'qualified' | 'proposal' | 'negotiation' | 'won' | 'lost';
  age: number; // how many days ago the lead was created (positive)
}

const LEAD_CTX: Record<string, LeadCtx> = {
  ld1: {
    title: 'Portal intern HR',
    company: 'NextGen Media',
    contact: 'Irina Vlad',
    valueEur: 12_000,
    assignee: 'p16',
    techLead: 'p3',
    stage: 'new',
    age: 30,
  },
  ld2: {
    title: 'App livrare mâncare',
    company: 'FoodFast SRL',
    contact: 'Bogdan Achim',
    valueEur: 40_000,
    assignee: 'p3',
    techLead: 'p7',
    stage: 'new',
    age: 34,
  },
  ld3: {
    title: 'Website prezentare',
    company: 'GreenBuild',
    contact: 'Maria Cojocaru',
    valueEur: 5_000,
    assignee: 'p16',
    techLead: 'p6',
    stage: 'new',
    age: 38,
  },
  ld4: {
    title: 'Platformă e-learning',
    company: 'EduPlatform SRL',
    contact: 'Simona Barbu',
    valueEur: 55_000,
    assignee: 'p1',
    techLead: 'p2',
    stage: 'qualified',
    age: 42,
  },
  ld5: {
    title: 'Integrare ERP',
    company: 'IndustriaPlus',
    contact: 'Cornel Pavel',
    valueEur: 48_000,
    assignee: 'p4',
    techLead: 'p8',
    stage: 'qualified',
    age: 46,
  },
  ld6: {
    title: 'Aplicație mobilă fitness',
    company: 'FitLife',
    contact: 'Alina Roșu',
    valueEur: 30_000,
    assignee: 'p3',
    techLead: 'p10',
    stage: 'qualified',
    age: 50,
  },
  ld7: {
    title: 'Marketplace B2B',
    company: 'TradeHub',
    contact: 'Victor Enache',
    valueEur: 80_000,
    assignee: 'p1',
    techLead: 'p2',
    stage: 'qualified',
    age: 54,
  },
  ld8: {
    title: 'Sistem ticketing',
    company: 'SupportCo',
    contact: 'Daniela Mureșan',
    valueEur: 25_000,
    assignee: 'p4',
    techLead: 'p9',
    stage: 'proposal',
    age: 58,
  },
  ld9: {
    title: 'Dashboard BI',
    company: 'DataViz Corp SRL',
    contact: 'Laura Munteanu',
    valueEur: 38_000,
    assignee: 'p3',
    techLead: 'p14',
    stage: 'proposal',
    age: 62,
  },
  ld10: {
    title: 'App rezervări',
    company: 'BookIt',
    contact: 'Rareș Tudor',
    valueEur: 22_000,
    assignee: 'p16',
    techLead: 'p10',
    stage: 'proposal',
    age: 66,
  },
  ld11: {
    title: 'Migrare cloud',
    company: 'LogiTrans SA',
    contact: 'Dan Ardelean',
    valueEur: 60_000,
    assignee: 'p2',
    techLead: 'p15',
    stage: 'negotiation',
    age: 70,
  },
  ld12: {
    title: 'Redesign UX',
    company: 'RetailHub SRL',
    contact: 'Oana Dragomir',
    valueEur: 18_000,
    assignee: 'p6',
    techLead: 'p6',
    stage: 'negotiation',
    age: 74,
  },
  ld13: {
    title: 'HR Management System',
    company: 'PeopleFirst SRL',
    contact: 'Carmen Iliescu',
    valueEur: 95_000,
    assignee: 'p1',
    techLead: 'p2',
    stage: 'won',
    age: 78,
  },
  ld14: {
    title: 'Analytics Dashboard',
    company: 'DataViz Corp SRL',
    contact: 'Laura Munteanu',
    valueEur: 65_000,
    assignee: 'p3',
    techLead: 'p14',
    stage: 'won',
    age: 82,
  },
  ld15: {
    title: 'App internă',
    company: 'OldCorp',
    contact: 'Nicolae Dima',
    valueEur: 15_000,
    assignee: 'p16',
    techLead: 'p9',
    stage: 'lost',
    age: 86,
  },
};

// How far each stage has progressed. Later stages get more notes/interactions.
const STAGE_NAMES_RO: Record<LeadCtx['stage'], string> = {
  new: 'New',
  qualified: 'Qualified',
  proposal: 'Proposal Sent',
  negotiation: 'Negotiation',
  won: 'Won',
  lost: 'Lost',
};

// Build the two/three meeting notes for a lead given its stage & context.
function notesFor(ctx: LeadCtx): NoteSeed[] {
  const { company, contact, valueEur, assignee, techLead, age } = ctx;
  const sales = personName(assignee);
  const tech = personName(techLead);
  const budgetRon = (valueEur * 5).toLocaleString('ro-RO');
  const attendees = `${sales} (CCC), ${tech} (CCC), ${contact} (${company})`;

  // Discovery call — always present (oldest).
  const discovery: NoteSeed = {
    key: 'discovery',
    title: `Discovery call — ${ctx.title} (${company})`,
    dayOffset: -(age - 2),
    attendees: `${sales} (CCC), ${contact} (${company})`,
    meetingType: 'DISCOVERY',
    content:
      `Prima discuție cu ${contact} de la ${company} despre „${ctx.title}”. ${contact} a descris ` +
      `problema de business și obiectivele așteptate. Am parcurs contextul actual, utilizatorii ` +
      `vizați și constrângerile principale. ${sales} a prezentat pe scurt experiența Clean Code ` +
      `Culture pe proiecte similare. Bugetul orientativ discutat este în jur de ${valueEur.toLocaleString('ro-RO')} EUR ` +
      `(~${budgetRon} RON), iar clientul vrea o livrare pe faze. Am convenit o sesiune tehnică de ` +
      `aprofundare a cerințelor împreună cu un specialist din echipă.`,
    summary: `Discovery ${company}: nevoie pentru „${ctx.title}”, buget orientativ ~${valueEur.toLocaleString('ro-RO')} EUR, livrare pe faze. Urmează sesiune tehnică.`,
    decisions: `- Livrare pe faze, cu un MVP în prima etapă\n- Sesiune tehnică de aprofundare a cerințelor`,
    actionItems: `- [ ] Pregătire agendă sesiune tehnică (${sales})\n- [ ] Strângere materiale/context de la client (${contact})`,
    keyFacts: `- Client: ${company} — contact ${contact}\n- Buget orientativ ~${valueEur.toLocaleString('ro-RO')} EUR (~${budgetRon} RON)`,
    openQuestions: `- Care este termenul țintă pentru prima fază?\n- Există integrări cu sisteme existente?`,
  };

  // Technical requirements meeting — always present.
  const technical: NoteSeed = {
    key: 'tech',
    title: `Sesiune tehnică & cerințe — ${ctx.title}`,
    dayOffset: -Math.max(4, Math.round(age * 0.55)),
    attendees,
    meetingType: 'REVIEW',
    content:
      `${tech} a condus sesiunea tehnică pentru „${ctx.title}”. Am detaliat cerințele funcționale și ` +
      `nefuncționale, fluxurile principale și volumul estimat. ${contact} a confirmat prioritățile ` +
      `pentru MVP și a ridicat câteva cerințe de integrare. Am discutat abordarea de arhitectură ` +
      `propusă de ${tech} și am estimat efortul pe faze. ${contact} a fost mulțumit de claritatea ` +
      `planului tehnic și a cerut o ofertă detaliată cu timeline și costuri.`,
    summary: `Sesiune tehnică ${company}: cerințe MVP clarificate, arhitectură propusă, estimare pe faze. Clientul cere ofertă detaliată.`,
    decisions: `- Scope MVP înghețat pentru ofertă\n- Arhitectură pe faze, cu integrările cerute în faza 2`,
    actionItems: `- [ ] Redactare ofertă detaliată cu timeline (${sales})\n- [ ] Estimare efort pe module (${tech})`,
    keyFacts: `- Cerințe MVP confirmate de ${contact}\n- Integrări identificate pentru faza 2`,
    openQuestions: `- Confirmăm bugetul total după estimarea pe module?`,
  };

  const notes: NoteSeed[] = [discovery, technical];

  // Later stages add a proposal / negotiation / closing note (3rd note).
  if (
    ctx.stage === 'proposal' ||
    ctx.stage === 'negotiation' ||
    ctx.stage === 'won' ||
    ctx.stage === 'lost'
  ) {
    notes.push({
      key: 'proposal',
      title: `Prezentare ofertă — ${ctx.title}`,
      dayOffset: -Math.max(3, Math.round(age * 0.3)),
      attendees,
      meetingType: 'STATUS',
      content:
        `Am prezentat oferta pentru „${ctx.title}” către ${contact}. Oferta acoperă ${valueEur.toLocaleString('ro-RO')} EUR ` +
        `(~${budgetRon} RON) pe ${ctx.stage === 'won' ? 'întregul scope' : 'scope-ul agreat'}, cu livrare pe faze și ` +
        `un plan de plată la milestone-uri. ${contact} a parcurs propunerea tehnică și comercială. ` +
        (ctx.stage === 'won'
          ? `Clientul a confirmat acceptarea ofertei și trecem la semnarea contractului.`
          : ctx.stage === 'lost'
            ? `Clientul a apreciat oferta dar a semnalat constrângeri de buget intern; decizia a fost amânată/declinată.`
            : `Clientul are câteva observații pe preț și timeline pe care le clarificăm în următoarea rundă.`),
      summary:
        `Ofertă prezentată ${company} pentru „${ctx.title}” (~${valueEur.toLocaleString('ro-RO')} EUR). ` +
        (ctx.stage === 'won'
          ? 'Acceptată — urmează contractul.'
          : ctx.stage === 'lost'
            ? 'Declinată din motive de buget.'
            : 'În discuție pe preț/timeline.'),
      decisions:
        ctx.stage === 'won'
          ? `- Ofertă acceptată\n- Trecere la contract și kickoff`
          : ctx.stage === 'lost'
            ? `- Oportunitate închisă (buget client indisponibil)`
            : `- Rundă de negociere pe preț și timeline`,
      actionItems:
        ctx.stage === 'won'
          ? `- [ ] Pregătire contract (${sales})\n- [ ] Planificare kickoff (${tech})`
          : ctx.stage === 'lost'
            ? `- [ ] Follow-up peste 6 luni (${sales})`
            : `- [ ] Revizuire ofertă conform feedback (${sales})\n- [ ] Re-estimare timeline (${tech})`,
      keyFacts: `- Valoare ofertă ~${valueEur.toLocaleString('ro-RO')} EUR (~${budgetRon} RON)`,
      openQuestions:
        ctx.stage === 'won'
          ? '- Data de start pentru kickoff?'
          : ctx.stage === 'lost'
            ? '- Reluăm discuția în următorul an fiscal?'
            : '- Ce marjă de negociere acceptăm pe preț?',
    });
  }

  return notes;
}

// Build the interaction timeline (>=5) for a lead.
function interactionsFor(ctx: LeadCtx): InteractionSeed[] {
  const { assignee, techLead, contact, age, stage } = ctx;
  const sales = assignee;
  const items: InteractionSeed[] = [];

  // 1. status: created
  items.push({ kind: 'status', dayOffset: -age, hour: 9, who: sales, field: 'created' });
  // 2. email: intro / follow-up after discovery
  items.push({
    kind: 'email',
    dayOffset: -(age - 1),
    hour: 11,
    who: sales,
    text: `follow-up discovery trimis către ${contact} cu rezumat și pașii următori`,
  });
  // 3. call: schedule technical session
  items.push({
    kind: 'call',
    dayOffset: -(age - 3),
    hour: 15,
    who: sales,
    text: `apel de stabilire a sesiunii tehnice și clarificare cerințe inițiale`,
  });
  // 4. meeting: the technical session (links to the tech note conceptually)
  items.push({
    kind: 'meeting',
    dayOffset: -Math.max(4, Math.round(age * 0.55)),
    hour: 10,
    who: techLead,
    text: `sesiune tehnică & cerințe cu ${contact}`,
  });
  // 5. message: quick WhatsApp confirmation
  items.push({
    kind: 'message',
    dayOffset: -Math.max(3, Math.round(age * 0.5)),
    hour: 17,
    who: sales,
    text: `${contact} a confirmat pe WhatsApp că așteaptă oferta`,
  });
  // 6. comment: internal note from the rep
  items.push({
    kind: 'comment',
    dayOffset: -Math.max(3, Math.round(age * 0.45)),
    hour: 18,
    who: sales,
    text: `Lead interesat și implicat. Buget orientativ ~${ctx.valueEur.toLocaleString('ro-RO')} EUR. Pregătim ofertă detaliată.`,
  });

  // Stage-dependent later interactions.
  if (stage === 'proposal' || stage === 'negotiation' || stage === 'won' || stage === 'lost') {
    items.push({
      kind: 'status',
      dayOffset: -Math.max(5, Math.round(age * 0.4)),
      hour: 9,
      who: sales,
      field: 'stageId',
      newValue: STAGE_NAMES_RO['proposal'],
    });
    items.push({
      kind: 'email',
      dayOffset: -Math.max(3, Math.round(age * 0.3)),
      hour: 12,
      who: sales,
      text: `ofertă detaliată trimisă către ${contact}`,
    });
  }
  if (stage === 'negotiation') {
    items.push({
      kind: 'call',
      dayOffset: -Math.max(2, Math.round(age * 0.15)),
      hour: 14,
      who: sales,
      text: `rundă de negociere pe preț și timeline cu ${contact}`,
    });
    items.push({
      kind: 'status',
      dayOffset: -Math.max(2, Math.round(age * 0.2)),
      hour: 9,
      who: sales,
      field: 'stageId',
      newValue: STAGE_NAMES_RO['negotiation'],
    });
  }
  if (stage === 'won') {
    items.push({
      kind: 'status',
      dayOffset: -Math.max(2, Math.round(age * 0.12)),
      hour: 9,
      who: sales,
      field: 'stageId',
      newValue: STAGE_NAMES_RO['won'],
    });
    items.push({
      kind: 'comment',
      dayOffset: -Math.max(1, Math.round(age * 0.1)),
      hour: 16,
      who: assignee,
      text: `Ofertă acceptată! Trecem la contract și planificăm kickoff-ul.`,
    });
  }
  if (stage === 'lost') {
    items.push({
      kind: 'status',
      dayOffset: -Math.max(2, Math.round(age * 0.12)),
      hour: 9,
      who: sales,
      field: 'stageId',
      newValue: STAGE_NAMES_RO['lost'],
    });
    items.push({
      kind: 'comment',
      dayOffset: -Math.max(1, Math.round(age * 0.1)),
      hour: 16,
      who: assignee,
      text: `Oportunitate închisă — buget client indisponibil. De reluat peste ~6 luni.`,
    });
  }

  // Sort newest-first to match the timeline render order.
  return items.sort((a, b) => {
    const ta = new Date(isoDateTime(a.dayOffset, a.hour)).getTime();
    const tb = new Date(isoDateTime(b.dayOffset, b.hour)).getTime();
    return tb - ta;
  });
}

// Build the actions (scheduled activities) for a lead.
function actionsFor(ctx: LeadCtx): ActionSeed[] {
  const { assignee, techLead, stage, age } = ctx;
  const open = stage !== 'won' && stage !== 'lost';
  const actions: ActionSeed[] = [
    {
      type: 'CALL',
      title: 'Apel de follow-up după discovery',
      dayOffset: -(age - 3),
      who: assignee,
      completed: true,
      completedOffset: -(age - 3),
    },
    {
      type: 'MEETING',
      title: 'Sesiune tehnică & cerințe',
      dayOffset: -Math.max(4, Math.round(age * 0.55)),
      who: techLead,
      completed: true,
      completedOffset: -Math.max(4, Math.round(age * 0.55)),
    },
    {
      type: 'PROPOSAL',
      title: 'Trimitere ofertă detaliată',
      dayOffset: -Math.max(3, Math.round(age * 0.3)),
      who: assignee,
      completed: stage !== 'new',
      completedOffset: -Math.max(3, Math.round(age * 0.3)),
    },
  ];
  if (open) {
    actions.push({
      type: 'FOLLOW_UP',
      title: 'Follow-up pe decizie',
      dayOffset: 3,
      who: assignee,
      completed: false,
    });
  } else if (stage === 'won') {
    actions.push({
      type: 'TASK',
      title: 'Pregătire contract & kickoff',
      dayOffset: 2,
      who: assignee,
      completed: false,
    });
  } else {
    actions.push({
      type: 'FOLLOW_UP',
      title: 'Reluare discuție peste 6 luni',
      dayOffset: 180,
      who: assignee,
      completed: false,
    });
  }
  return actions;
}

// --- Public API ------------------------------------------------------------

const LEAD_IDS = Object.keys(LEAD_CTX);

let notesCache: MeetingNote[] | null = null;

function allNotes(): MeetingNote[] {
  if (notesCache) return notesCache;
  const out: MeetingNote[] = [];
  for (const id of LEAD_IDS) {
    for (const seed of notesFor(LEAD_CTX[id])) out.push(toNote(id, seed));
  }
  notesCache = out;
  return out;
}

// Meeting notes for a single lead (drives /api/meeting-notes?leadId=).
export function meetingNotesForLead(leadId: string): MeetingNote[] {
  return allNotes().filter((n) => n.leadId === leadId);
}

// A single lead meeting note by id (drives /api/meeting-notes/:id).
export function buildLeadMeetingNote(id: string): MeetingNote | null {
  return allNotes().find((n) => n.id === id) ?? null;
}

// Unified interaction timeline for a lead (drives /api/leads/:id/timeline).
export function leadTimeline(leadId: string): LeadTimelineItem[] {
  const ctx = LEAD_CTX[leadId];
  if (!ctx) return [];
  return interactionsFor(ctx).map((s, i) => toTimelineItem(leadId, i, s));
}

// Scheduled actions for a lead (drives /api/leads/:id/actions).
export function leadActions(leadId: string): LeadAction[] {
  const ctx = LEAD_CTX[leadId];
  if (!ctx) return [];
  return actionsFor(ctx).map((s, i) => toAction(leadId, i, s));
}

// Counts used to keep the lead summary cards coherent with the detail data.
export function leadInteractionCounts(leadId: string): {
  actionCount: number;
  commentCount: number;
  lastContactedAt: string | null;
} {
  const ctx = LEAD_CTX[leadId];
  if (!ctx) return { actionCount: 0, commentCount: 0, lastContactedAt: null };
  const interactions = interactionsFor(ctx);
  const commentCount = interactions.filter((i) => i.kind === 'comment').length;
  const actionCount = actionsFor(ctx).filter((a) => !a.completed).length;
  // Most recent interaction timestamp (interactions are sorted newest-first).
  const latest = interactions[0];
  const lastContactedAt = latest ? isoDateTime(latest.dayOffset, latest.hour) : null;
  return { actionCount, commentCount, lastContactedAt };
}
